/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Immutable Audit Logs View with TanStack DataTable (Task 11.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ShieldCheck,
  Eye,
  RefreshCw,
  Filter,
  X,
  FileCode,
  Calendar,
  Globe,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import { DataTable, type ColumnDef } from '../components/table/DataTable';
import type { AdminAuditLogItem } from '@sofia/shared-types';

export const AdminAuditLogsView: React.FC = () => {
  const { t } = useI18n();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [actionFilter, setActionFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedLog, setSelectedLog] = useState<AdminAuditLogItem | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-audit-logs', pageIndex, pageSize, actionFilter, searchQuery],
    queryFn: async () => {
      const res = await adminClient.get('/api/v1/admin/audit-logs', {
        page: pageIndex + 1,
        limit: pageSize,
        action: actionFilter || undefined,
        search: searchQuery || undefined,
      });
      return res;
    },
  });

  const payload = data?.data || data;
  const logsList: AdminAuditLogItem[] = payload?.items || [];
  const totalCount = payload?.meta?.totalCount || 0;

  const columns: ColumnDef<AdminAuditLogItem>[] = [
    {
      id: 'createdAt',
      header: t('admin.audit.timestamp'),
      accessorKey: 'createdAt',
      cell: ({ value }) => (
        <div className="flex items-center gap-1.5 font-mono text-[11px] text-muted-fg">
          <Calendar className="w-3.5 h-3.5" />
          <span>{value ? new Date(value).toLocaleString() : '—'}</span>
        </div>
      ),
    },
    {
      id: 'action',
      header: t('admin.audit.action'),
      accessorKey: 'action',
      cell: ({ value }) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono font-semibold bg-primary/10 text-primary border border-primary/20">
          {value}
        </span>
      ),
    },
    {
      id: 'adminUserId',
      header: t('admin.audit.adminUser'),
      accessorKey: 'adminUserId',
      cell: ({ row, value }) => (
        <div className="flex flex-col">
          <span className="font-mono text-[11px] text-foreground font-medium">
            {row.adminUserName || value || 'system'}
          </span>
          {row.adminUserEmail && (
            <span className="text-[10px] text-muted-fg font-mono">{row.adminUserEmail}</span>
          )}
        </div>
      ),
    },
    {
      id: 'target',
      header: t('admin.audit.target'),
      accessorFn: (row) => (row.targetType ? `${row.targetType}:${row.targetId || ''}` : '—'),
      cell: ({ value }) => (
        <span className="text-[11px] font-mono text-muted-fg bg-surface-elevated px-2 py-0.5 rounded border border-surface-border">
          {value}
        </span>
      ),
    },
    {
      id: 'clientIp',
      header: t('admin.audit.ipAddress'),
      accessorKey: 'clientIp',
      cell: ({ value }) => (
        <div className="flex items-center gap-1 text-[11px] font-mono text-muted-fg">
          <Globe className="w-3 h-3 opacity-60" />
          <span>{value || 'unknown'}</span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: t('common.actions'),
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => setSelectedLog(row)}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-surface-border bg-surface hover:bg-surface-elevated text-foreground transition-colors"
          title={t('common.view')}
        >
          <Eye className="w-3.5 h-3.5 text-primary" />
          <span>{t('common.view')}</span>
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* View Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-heading font-bold text-foreground">
              {t('admin.audit.title')}
            </h2>
          </div>
          <p className="text-xs text-muted-fg mt-0.5">
            {t('admin.audit.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border border-surface-border bg-surface text-foreground">
            <Filter className="w-3.5 h-3.5 text-muted-fg" />
            <select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPageIndex(0);
              }}
              className="bg-transparent text-xs text-foreground focus:outline-none"
            >
              <option value="">{t('admin.audit.allActions')}</option>
              <option value="ai_model.created">ai_model.created</option>
              <option value="ai_model.updated">ai_model.updated</option>
              <option value="system.config_updated">system.config_updated</option>
              <option value="order.approved">order.approved</option>
              <option value="order.rejected">order.rejected</option>
            </select>
          </div>

          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-surface-border bg-surface hover:bg-surface-elevated text-foreground transition-colors disabled:opacity-40"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin text-primary' : ''}`} />
            <span>{t('common.refresh')}</span>
          </button>
        </div>
      </div>

      {/* Main DataTable with Server-side Pagination */}
      <DataTable<AdminAuditLogItem>
        columns={columns}
        data={logsList}
        isLoading={isLoading}
        error={error ? (error as Error).message : null}
        isServerSide={true}
        totalCount={totalCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={(p) => setPageIndex(p)}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPageIndex(0);
        }}
        searchPlaceholder={t('admin.audit.filterAction')}
        onSearchChange={(q) => {
          setSearchQuery(q);
          setPageIndex(0);
        }}
        exportFileName={`admin_audit_logs_${new Date().toISOString().slice(0, 10)}.csv`}
        emptyTitle={t('admin.audit.emptyState') || 'No Audit Logs Recorded'}
        emptyDescription="Administrative interventions, key pool updates, and permission changes will appear in this immutable journal."
      />

      {/* State Inspection Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-surface border border-surface-border rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated/40">
              <div className="flex items-center gap-2">
                <FileCode className="w-4 h-4 text-primary" />
                <h3 className="font-heading font-bold text-sm text-foreground">
                  {t('admin.audit.detailsModalTitle')}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="p-1.5 rounded-lg border border-surface-border hover:bg-surface-elevated text-muted-fg hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5 text-xs">
              {/* Metadata Badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-surface-elevated/50 p-3.5 rounded-xl border border-surface-border">
                <div>
                  <span className="text-[10px] text-muted-fg uppercase tracking-wider block">Action</span>
                  <span className="font-mono font-semibold text-primary">{selectedLog.action}</span>
                </div>
                <div>
                  <span className="text-[10px] text-muted-fg uppercase tracking-wider block">Admin User</span>
                  <span className="font-mono text-foreground">{selectedLog.adminUserName || selectedLog.adminUserId}</span>
                </div>
                <div>
                  <span className="text-[10px] text-muted-fg uppercase tracking-wider block">Target</span>
                  <span className="font-mono text-muted-fg">{selectedLog.targetType}:{selectedLog.targetId || '—'}</span>
                </div>
                <div>
                  <span className="text-[10px] text-muted-fg uppercase tracking-wider block">Client IP</span>
                  <span className="font-mono text-muted-fg">{selectedLog.clientIp || '—'}</span>
                </div>
              </div>

              {/* State Diff Grids */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
                    {t('admin.audit.beforeState')}
                  </span>
                  <pre className="p-3.5 rounded-xl bg-surface-elevated border border-surface-border font-mono text-[11px] text-foreground overflow-x-auto max-h-60">
                    {selectedLog.beforeState
                      ? JSON.stringify(selectedLog.beforeState, null, 2)
                      : '// null (no previous state)'}
                  </pre>
                </div>

                <div className="space-y-1.5">
                  <span className="text-[11px] font-semibold text-primary uppercase tracking-wider">
                    {t('admin.audit.afterState')}
                  </span>
                  <pre className="p-3.5 rounded-xl bg-surface-elevated border border-surface-border font-mono text-[11px] text-foreground overflow-x-auto max-h-60">
                    {selectedLog.afterState
                      ? JSON.stringify(selectedLog.afterState, null, 2)
                      : '// null (mutation had no payload)'}
                  </pre>
                </div>
              </div>
            </div>

            <div className="px-6 py-3 border-t border-surface-border bg-surface-elevated/40 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-surface border border-surface-border hover:bg-surface-elevated text-foreground transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
